import { useState } from "react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Activity, ArrowRight, Loader2 } from "lucide-react";

export default function AuthScreen() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("demo@cadence.dev");
  const [password, setPassword] = useState("demo1234");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }

  return (
    <div className="h-screen w-full flex">
      {/* left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[44%] bg-[#0A0A0A] text-white p-12 relative overflow-hidden">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white text-black grid place-items-center">
            <Activity className="w-5 h-5" strokeWidth={2} />
          </div>
          <span className="font-heading font-700 text-xl tracking-tight">Cadence</span>
        </div>
        <div className="relative z-10">
          <h1 className="font-heading font-800 text-5xl leading-[1.05] tracking-tight">
            One operator console.<br />One active task.<br />
            <span className="text-gray-500">Your machine. Your models.</span>
          </h1>
          <p className="mt-6 text-gray-400 text-[15px] leading-relaxed max-w-md">
            A task-first AI coding workspace. Pair your local host, keep your model
            subscriptions where they live, and drive everything from one composer — anywhere.
          </p>
        </div>
        <div className="flex gap-6 text-xs text-gray-500 relative z-10">
          <span>Local-first relay</span><span>Approval-gated edits</span><span>Checkpoints</span>
        </div>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
      </div>

      {/* form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#F9FAFB]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-lg bg-black text-white grid place-items-center">
              <Activity className="w-5 h-5" />
            </div>
            <span className="font-heading font-700 text-xl">Cadence</span>
          </div>
          <h2 className="font-heading font-700 text-2xl tracking-tight text-gray-900">
            {mode === "login" ? "Welcome back" : "Create your workspace"}
          </h2>
          <p className="text-sm text-gray-500 mt-1 mb-6">
            {mode === "login" ? "Sign in to your Cadence workspace." : "Start building in minutes."}
          </p>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Name" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500" />
            )}
            <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500" />
            <input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500" />
            {error && <div data-testid="auth-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="auth-submit-button" disabled={loading}
              className="w-full bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{mode === "login" ? "Sign in" : "Create account"} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-5 text-sm text-gray-500">
            {mode === "login" ? "New to Cadence?" : "Already have an account?"}{" "}
            <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-blue-600 font-medium hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>
          <div className="mt-6 text-xs text-gray-400 bg-white border border-gray-200 rounded-lg px-3 py-2">
            Demo account is pre-filled — a live host is already paired. Just sign in.
          </div>
        </div>
      </div>
    </div>
  );
}
